/**
 * ⚠️⚠️ **DEVELOPMENT IS CROSS-ORIGIN AND PRODUCTION IS NOT, AND THAT ASYMMETRY IS ON PURPOSE
 * (`decisions.md` R-22).**
 *
 * Deployed, `VITE_API_BASE_URL` is the relative path `/api`, proxied to the API by the app's own
 * nginx (`apps/web/nginx.conf`) — so the browser only ever talks to the origin it loaded the page
 * from. That removes the preflight from every request and, with it, a whole class of failure whose
 * symptom names the wrong subsystem: a 502 from an upstream carries no `Access-Control-Allow-Origin`
 * and so surfaces in the browser as a CORS error (`hard-won.md` D2-1).
 *
 * In development it stays an absolute cross-origin URL, and a Vite dev proxy is still refused. The
 * reasoning has flipped rather than gone: dev is now the STRICTER of the two configurations, so a
 * cookie or CORS mistake still fails on a laptop — and it can no longer be a mistake that only
 * production has.
 *
 * ⚠️ **`credentials: 'include'` on every request, from the first one.** Same-origin does not need
 * it and it costs nothing there; development does. A cross-origin `Set-Cookie` is only stored if
 * the request that produced it asked for credentials, and the outgoing project spent five work
 * packages on a refresh cookie discarded at issue because the sign-in call did not ask while the
 * refresh call correctly did.
 *
 * ⚠️⚠️ **THE COOKIE'S `Path` AND THE PROXY PREFIX ARE COUPLED.** `auth.controller.ts` scopes the
 * refresh cookie to `path: '/auth'`; behind the `/api` prefix the browser would never send it back
 * to `/api/auth/refresh`, so `apps/web/nginx.conf` rewrites the path with `proxy_cookie_path`.
 * Nothing about that failure is visible at sign-in — it is a silent sign-out an hour later.
 */

/**
 * ⚠️ **Read at CALL time, not at module scope, and it refuses an empty value.**
 *
 * A `const BASE = import.meta.env.VITE_API_BASE_URL` at the top of this file was the first
 * version, and it was wrong twice over. It captures the value when the module is imported,
 * which makes it untestable — but worse, a missing variable became the string `undefined`,
 * so every request went to `undefined/health` and the screen reported "the API is not
 * responding". A configuration mistake that presents as a server fault is the most
 * expensive kind, and this is the same rule the API applies to its own environment: refuse,
 * do not default.
 */
export function apiBase(): string {
  const base: unknown = import.meta.env.VITE_API_BASE_URL;
  if (typeof base !== 'string' || base.trim() === '') {
    throw new Error(
      'VITE_API_BASE_URL is not set, so the app does not know where the API is. ' +
        'It is documented in .env.example; Vite only exposes variables prefixed VITE_.',
    );
  }
  return base.replace(/\/$/, '');
}

export function apiUrl(path: string): string {
  /**
   * ⚠️⚠️ **A PATH THAT IS NOT A PATH IS REFUSED HERE, BECAUSE THE ALTERNATIVE BLAMES THE SERVER.**
   * `apiBase()` above already refuses an empty base, and its reason applies to the other half of the
   * string verbatim: *"a configuration mistake that presents as a server fault is the most expensive
   * kind."*
   *
   * ⚠️ **Measured, from the running app.** A stale Vite pre-bundle (hard-won C5-8, S2-5) served the
   * browser a copy of `@forecastify/shared` from before `CONTROL_PANEL_PATHS.plans` existed, so the
   * screen called this with `undefined`, the template produced `http://localhost:3000undefined`,
   * `fetch` rejected, and the client answered `unreachable` — *"the server is not responding"*. The
   * server was fine, and nothing in the message pointed at the dependency cache.
   */
  /* ⚠️ Widened to `unknown` first, and `apiBase()` above uses the same idiom for the same reason:
   * the signature says this cannot happen, the runtime says otherwise, and a check the types call
   * impossible is exactly the check worth having. Narrowing here also keeps lint honest — with
   * `path` still typed `string`, `String(path)` reads as a pointless conversion. */
  const given: unknown = path;
  if (typeof given !== 'string' || !given.startsWith('/')) {
    throw new Error(
      `apiUrl was given ${String(given)}, which is not a path. A missing constant reaching here ` +
        'usually means a stale dependency pre-bundle — run `pnpm frontend:fresh` (see ' +
        'docs/environment/hard-won.md C5-8) rather than looking for a server fault.',
    );
  }
  return `${apiBase()}${given}`;
}

export async function apiGet(path: string): Promise<unknown> {
  const response = await fetch(apiUrl(path), { credentials: 'include' });
  if (!response.ok) throw new Error(`the API answered ${String(response.status)}`);
  return response.json();
}
